import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { API, apiErrorMessage } from "@/lib/api";

const startGoogleAuth = () => {
  window.location.href = `${API}/auth/google/login`;
};

export default function Login() {
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [tab, setTab] = useState("login");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back");
      } else {
        await register(form.email, form.password, form.name);
        toast.success("Account created");
      }
      nav("/client/dashboard");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07111D] text-white flex">
      {/* Left side: marketing */}
      <div className="hidden lg:flex flex-col justify-between p-12 w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#18BFFF]/10 via-transparent to-transparent pointer-events-none" />
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#18BFFF] flex items-center justify-center text-[#07111D] font-bold text-sm">ED</div>
            <span className="font-display font-semibold">Elite Digital</span>
          </Link>
        </div>
        <div className="relative">
          <h1 className="font-display text-5xl font-bold leading-[1.05] max-w-md">
            Your team's digital cards, <span className="text-[#18BFFF]">one login away.</span>
          </h1>
          <p className="mt-6 text-[#94A3B8] max-w-md">
            Every ticket, every QR, every vCard — synced across your organization.
            Upload your logo once and it shows up everywhere.
          </p>
        </div>
        <div className="relative text-xs text-[#4A5A73]">
          © {new Date().getFullYear()} Elite Digital d.o.o.
        </div>
      </div>

      {/* Right side: auth form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md" data-testid="login-panel">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-[#0B131E] border border-[#1E2D40] w-full grid grid-cols-2">
              <TabsTrigger value="login" data-testid="tab-login">Sign in</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Create account</TabsTrigger>
            </TabsList>

            <div className="mt-6 space-y-3">
              <SocialButton
                testid="google-login-btn"
                onClick={startGoogleAuth}
                icon={<GoogleGlyph />}
                label="Continue with Google"
              />
            </div>

            <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-[#4A5A73]">
              <div className="flex-1 h-px bg-[#1E2D40]" />
              or
              <div className="flex-1 h-px bg-[#1E2D40]" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              <TabsContent value="register" className="mt-0 space-y-4">
                <Fld label="Full name" icon={User}>
                  <Input required data-testid="input-name" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls} placeholder="Ana Kovač" />
                </Fld>
              </TabsContent>

              <Fld label="Email" icon={Mail}>
                <Input required type="email" data-testid="input-email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls} placeholder="you@company.com" />
              </Fld>
              <Fld label="Password" icon={Lock}>
                <Input required type="password" data-testid="input-password" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className={inputCls} placeholder="••••••••" minLength={6} />
              </Fld>

              <Button type="submit" disabled={busy} data-testid="submit-btn"
                      className="w-full bg-[#18BFFF] text-[#07111D] hover:bg-[#4DD0FF] font-semibold h-11">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {tab === "login" ? "Sign in" : "Create account"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/admin-portal/login" className="inline-flex items-center gap-1.5 text-xs text-[#4A5A73] hover:text-[#94A3B8] transition-colors" data-testid="admin-portal-link">
                <ShieldCheck className="w-3 h-3" />
                Admin portal
              </Link>
            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

const inputCls = "bg-[#0B131E] border-[#1E2D40] text-white placeholder:text-[#4A5A73] focus-visible:ring-[#18BFFF] focus-visible:border-[#18BFFF] h-11";

function Fld({ label, icon: Icon, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8] flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function SocialButton({ icon, label, onClick, disabled, badge, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={
        "w-full h-11 rounded-lg border border-[#1E2D40] px-4 flex items-center gap-3 text-sm transition-colors " +
        (disabled
          ? "bg-[#0B131E] text-[#4A5A73] cursor-not-allowed"
          : "bg-[#101B28] text-white hover:bg-[#162436]")
      }
    >
      <span className="w-5 h-5 shrink-0">{icon}</span>
      <span className="flex-1 text-left">{label}</span>
      {badge && <span className="text-[10px] uppercase tracking-wider text-[#4A5A73]">{badge}</span>}
    </button>
  );
}

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 48 48" width="20" height="20" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.3 29.4 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.7 6.8 29.1 5 24 5 13.5 5 5 13.5 5 24s8.5 19 19 19 19-8.5 19-19c0-1.2-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.7 6.8 29.1 5 24 5 16.3 5 9.7 9.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 43c5 0 9.6-1.9 13-5l-6-5.1C29.1 34.4 26.7 35 24 35c-5.3 0-9.8-3.3-11.3-8l-6.6 5.1C9.6 38.9 16.3 43 24 43z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.4-4.3 5.9l6 5.1C40 34.8 43 29.8 43 24c0-1.2-.1-2.4-.4-3.5z"/>
    </svg>
  );
}
function AppleGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden>
      <path d="M17.4 12.3c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.9-3.4-1.9-1.4-.1-2.8.9-3.5.9-.7 0-1.9-.9-3.1-.8-1.6 0-3 1-3.8 2.4C4 12.3 5.3 17 7 19.5c.8 1.2 1.8 2.6 3.1 2.5 1.2 0 1.7-.8 3.2-.8 1.5 0 1.9.8 3.2.8 1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.3-2.8-.1 0-2.4-.9-2.4-3.5zM15 5.6c.7-.9 1.2-2 1.1-3.1-1 0-2.3.7-3 1.6-.7.8-1.3 2-1.1 3 1.1.1 2.3-.6 3-1.5z" />
    </svg>
  );
}
function MicrosoftGlyph() {
  return (
    <svg viewBox="0 0 21 21" width="20" height="20" aria-hidden>
      <rect x="1" y="1" width="9" height="9" fill="#F35325" />
      <rect x="11" y="1" width="9" height="9" fill="#81BC06" />
      <rect x="1" y="11" width="9" height="9" fill="#05A6F0" />
      <rect x="11" y="11" width="9" height="9" fill="#FFBA08" />
    </svg>
  );
}
